'use client'

import {
  searchBestAvailable,
  type BestAvailableSearch,
  type BestAvailableSeat,
} from '@/lib/seat-map/best-available-search'

import { useState, useCallback, useRef } from 'react'
import { ChevronLeft, AlertTriangle } from 'lucide-react'
import type { SeatCategory } from '@/types/seating'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'

export type BestSeat = BestAvailableSeat

interface ScoredResult {
  seats: BestSeat[]
  score: number
  warning?: string
  /** What the page will charge for this pick, priced by the page itself. */
  allIn?: BestAvailablePrice | null
}

/**
 * A pick priced the way the page prices a selection: the per-seat all-in
 * figure when every seat in the pick costs the same, else null, plus the
 * selection total the Add to Cart bar would show.
 */
export interface BestAvailablePrice {
  perSeat: number | null
  total: number
}

interface SectionInfo {
  sectionKey: string
  name?: string
  viewImageUrl?: string | null
  sectionType?: string
}

interface BestAvailablePanelProps {
  eventId: string
  categories: SeatCategory[]
  sections: SectionInfo[]
  /** The pick, plus the search that produced it so a collision at hold time can re-run it. */
  onSeatsFound: (seats: BestSeat[], search: BestAvailableSearch) => void
  onSeatsPreview: (seatIds: string[] | null) => void
  sectionKey?: string | null
  focalPoint?: { x: number; y: number } | null
  /** Controlled order quantity — when provided, this is the single source of truth
   *  (also used by the page to cap manual seat selection). Falls back to internal state. */
  quantity?: number
  onQuantityChange?: (q: number) => void
  /** Prices a pick with the page's own all-in pricing, so a result card quotes
   *  exactly what the price key and Add to Cart show. The server's seat price
   *  is the bare base price (no fees, and the WHOLE table for a per-table
   *  section), so without this the card falls back to that base average. */
  priceResult?: (seats: BestSeat[]) => Promise<BestAvailablePrice>
}

export default function BestAvailablePanel({
  eventId,
  categories,
  sections,
  onSeatsFound,
  onSeatsPreview,
  sectionKey,
  focalPoint,
  quantity: controlledQuantity,
  onQuantityChange,
  priceResult,
}: BestAvailablePanelProps) {
  const [internalQuantity, setInternalQuantity] = useState(2)
  const quantity = controlledQuantity ?? internalQuantity
  const setQuantity = (q: number) => { onQuantityChange ? onQuantityChange(q) : setInternalQuantity(q) }
  const [categoryId, setCategoryId] = useState('')
  const [sortBy, setSortBy] = useState<'best' | 'price' | 'location'>('best')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<ScoredResult[] | null>(null)
  const lastSearchRef = useRef<BestAvailableSearch | null>(null)
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)

  const findBestAvailable = useCallback(async () => {
    setLoading(true)
    setError(null)
    setResults(null)
    setSelectedIdx(null)
    onSeatsPreview(null)
    try {
      const search: BestAvailableSearch = { quantity, categoryId: categoryId || undefined, sectionKey: sectionKey || undefined, sortBy, focalPoint }
      const { options, usedSectionKey, error: missReason } = await searchBestAvailable(eventId, search)
      lastSearchRef.current = { ...search, sectionKey: usedSectionKey }
      const data = { success: options.length > 0, data: { results: options }, error: missReason }
      if (data.success && data.data?.results?.length > 0) {
        // Priced before the cards render, so a card never flashes the bare
        // base price first. A pricing failure drops only that card's figure.
        const priced: ScoredResult[] = priceResult
          ? await Promise.all(data.data.results.map(async (r: ScoredResult) => ({
              ...r,
              allIn: await priceResult(r.seats).catch(() => null),
            })))
          : data.data.results
        setResults(priced)
      } else {
        setError(data.error || 'No seats found matching your criteria')
      }
    } catch {
      setError('Failed to find seats. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [eventId, quantity, categoryId, sectionKey, sortBy, focalPoint, onSeatsPreview, priceResult])

  const handleSelectResult = useCallback((idx: number) => {
    if (!results?.[idx]) return
    setSelectedIdx(idx)
    onSeatsPreview(null)
    onSeatsFound(results[idx].seats, lastSearchRef.current || { quantity, sortBy })
  }, [results, onSeatsFound, onSeatsPreview])

  const handleBack = useCallback(() => {
    setResults(null)
    setSelectedIdx(null)
    setError(null)
    onSeatsPreview(null)
  }, [onSeatsPreview])

  const baseFormatPrice = useFormatPrice()
  const formatPrice = (price: number | null) => {
    if (price == null) return ''
    return baseFormatPrice(price)
  }

  const formatSeatRange = (seats: BestSeat[]) => {
    const numbers = seats.map(s => s.seatNumber)
    if (numbers.length === 1) return `Seat ${numbers[0]}`
    // Check if consecutive numbers
    const nums = numbers.map(Number).filter(n => !isNaN(n))
    if (nums.length === numbers.length && nums.length > 1) {
      const sorted = [...nums].sort((a, b) => a - b)
      const isConsecutive = sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1)
      if (isConsecutive) return `Seats ${sorted[0]}-${sorted[sorted.length - 1]}`
    }
    return `Seats ${numbers.join(', ')}`
  }

  // Results view
  if (results && results.length > 0) {
    return (
      <div className="border border-surface-border rounded-xl overflow-hidden bg-surface">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-border">
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back to search"
            className="w-11 h-11 -my-2 -ml-3 flex items-center justify-center rounded-md hover:bg-surface-secondary transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-text-secondary" />
          </button>
          <span className="text-sm font-semibold text-text-primary">
            {results.length} Option{results.length !== 1 ? 's' : ''} Found
          </span>
          <span className="text-xs text-text-tertiary ml-auto">
            {quantity} seat{quantity !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Result cards */}
        <div className="max-h-[400px] overflow-y-auto">
          {results.map((result, idx) => {
            const firstSeat = result.seats[0]
            const cat = firstSeat.categoryId
              ? categories.find(c => c.id === firstSeat.categoryId)
              : null
            const section = sections.find(s => s.sectionKey === firstSeat.sectionKey)
            const viewImage = section?.viewImageUrl
            const isSelected = selectedIdx === idx
            const avgPrice = result.seats.reduce((sum, s) => sum + (s.price || 0), 0) / result.seats.length

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelectResult(idx)}
                onMouseEnter={() => onSeatsPreview(result.seats.map(s => s.seatId))}
                onMouseLeave={() => onSeatsPreview(null)}
                className={`w-full text-left px-4 py-3 border-b border-surface-border last:border-b-0 transition-colors ${
                  isSelected
                    ? 'bg-primary/10 border-l-2 border-l-primary'
                    : 'hover:bg-surface-secondary'
                }`}
              >
                <div className="flex gap-3">
                  {/* View from seat thumbnail */}
                  {viewImage && (
                    <div className="flex-shrink-0 w-28 h-20 rounded-lg overflow-hidden bg-surface-secondary">
                      <img
                        src={viewImage}
                        alt={`View from ${firstSeat.sectionName}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Seat info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {/* Category color dot */}
                      {cat && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                      )}
                      <span className="text-sm font-medium text-text-primary truncate">
                        {firstSeat.sectionName}
                      </span>
                      {/* Score badge */}
                      <span className="ml-auto text-[10px] font-medium text-text-tertiary bg-surface-secondary rounded-full px-1.5 py-0.5 flex-shrink-0">
                        {idx === 0 ? 'Best' : `#${idx + 1}`}
                      </span>
                    </div>

                    <p className="text-xs text-text-secondary">
                      {/* A table's "row" is designer filler (every Africa table's is "T1",
                          so T2 read "Row T1"); the section name already says which table. */}
                      {section?.sectionType === 'TABLE' || section?.sectionType === 'BOOTH'
                        ? formatSeatRange(result.seats)
                        : <>Row {firstSeat.rowLabel} · {formatSeatRange(result.seats)}</>}
                    </p>

                    <div className="flex items-center gap-2 mt-1">
                      {priceResult ? (
                        result.allIn && result.allIn.total > 0 && (
                          <span className="text-xs font-semibold text-text-primary">
                            {result.allIn.perSeat != null
                              ? `${formatPrice(result.allIn.perSeat)}/ea`
                              : `${formatPrice(result.allIn.total)} total`}
                          </span>
                        )
                      ) : avgPrice > 0 && (
                        <span className="text-xs font-semibold text-text-primary">
                          {formatPrice(avgPrice)}/ea
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Search again */}
        <div className="px-4 py-3 border-t border-surface-border">
          <button
            type="button"
            onClick={handleBack}
            className="w-full py-2 min-h-[44px] text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Search again
          </button>
        </div>
      </div>
    )
  }

  // Input form view
  return (
    <div className="border border-surface-border rounded-xl overflow-hidden bg-surface">
      <div className="px-4 py-3 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          <span className="text-sm font-semibold text-text-primary">Best Available</span>
        </div>
      </div>

      <div className="px-4 pb-4 pt-3 space-y-3">
        <div className="flex items-center gap-3">
          <label className="text-xs text-text-tertiary flex-shrink-0 w-16">Tickets</label>
          <select
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value))}
            className="flex-1 px-3 py-1.5 min-h-[44px] lg:min-h-0 border border-surface-border rounded-lg text-base lg:text-sm bg-surface-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
              <option key={n} value={n}>{n} seat{n !== 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>

        {/* Sort mode */}
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-text-tertiary flex-shrink-0 w-16">Sort by</label>
          <div className="flex-1 flex gap-1">
            <button type="button" onClick={() => setSortBy('best')}
              className={`flex-1 px-2 py-1.5 min-h-[44px] lg:min-h-0 text-xs font-medium rounded-md transition-colors ${sortBy === 'best' ? 'bg-primary text-text-inverse' : 'bg-surface-secondary text-text-secondary hover:bg-surface-border'}`}>
              Best
            </button>
            <button type="button" onClick={() => setSortBy('price')}
              className={`flex-1 px-2 py-1.5 min-h-[44px] lg:min-h-0 text-xs font-medium rounded-md transition-colors ${sortBy === 'price' ? 'bg-primary text-text-inverse' : 'bg-surface-secondary text-text-secondary hover:bg-surface-border'}`}>
              Price
            </button>
            {focalPoint && (
              <button type="button" onClick={() => setSortBy('location')}
                className={`flex-1 px-2 py-1.5 min-h-[44px] lg:min-h-0 text-xs font-medium rounded-md transition-colors ${sortBy === 'location' ? 'bg-primary text-text-inverse' : 'bg-surface-secondary text-text-secondary hover:bg-surface-border'}`}>
                Location
              </button>
            )}
          </div>
        </div>

        {categories.length > 1 && (
          <div className="flex items-center gap-3">
            <label className="text-xs text-text-tertiary flex-shrink-0 w-16">Category</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="flex-1 px-3 py-1.5 min-h-[44px] lg:min-h-0 border border-surface-border rounded-lg text-base lg:text-sm bg-surface-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Any category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          onClick={findBestAvailable}
          disabled={loading}
          className="w-full py-2.5 min-h-[44px] bg-primary text-text-inverse rounded-lg text-sm font-semibold hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {loading ? 'Finding best seats...' : 'Find Best Seats'}
        </button>

        {error && (
          <p className="text-xs text-error text-center">{error}</p>
        )}
      </div>
    </div>
  )
}
